import type { ReactNode } from 'react';
import { PageHeader } from './PageHeader';

/** The one frame every list / dashboard page uses, so they all share the exact
 *  same page padding, width cap, vertical rhythm and header layout.
 *
 *  Header row: eyebrow / title / subtitle on the left (via `PageHeader`) and an
 *  optional `actions` slot on the right (a view toggle, a "New …" button, …).
 *  On a phone the actions drop onto their own row under the title (the row is a
 *  column until `sm`); from `sm` up they sit to the right of the header.
 *
 *  Everything below the header is `children` — filters, a table, a calendar —
 *  spaced by the wrapper's `space-y`. The table's own card owns its scroll +
 *  sticky header (see the table theme in AppUIProvider), so pages don't manage
 *  scrolling themselves. */
export function ListPage({
  eyebrow,
  title,
  subtitle,
  size,
  actions,
  children,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  size?: 'md' | 'lg';
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="max-w-295 space-y-6 px-4 pb-16 pt-7 sm:px-7">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader eyebrow={eyebrow} title={title} subtitle={subtitle} size={size} />
        {actions ? (
          <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div>
        ) : null}
      </div>
      {children}
    </div>
  );
}
